import { createStylesParams, StyleParamType } from '@wix/tpa-settings';
import { StyleType } from '@wix/editor-platform-ooi-metadata-resolver';
import { CSS_PROPERTIES } from '@wix/component-protocol/schema';
import {
  getSidesPaddingDefault,
  LAYOUT_DEFAULTS,
} from '../../constants/layoutConstants';
import { AlignmentOptions } from '../common/AlignmentThumbnails/AlignmentOptions';
import {
  isEditor3EditorContext,
  isStudio2EditorContext,
} from '../utils/platform';

export type ILayoutParams = {
  socialButtonsFirst: StyleParamType.Number;
  socialButtonsStyle: StyleParamType.Number;
  socialButtonsSpacing: StyleParamType.Number;
  spacingBetweenFormElements: StyleParamType.Number;
  sidesPadding: StyleParamType.Number;
  topAndBottomPadding: StyleParamType.Number;
  fullSubmitButtonWidth: StyleParamType.Boolean;
  logoSize: StyleParamType.Number;
  logoAlignment: StyleParamType.Number;
  submitButtonAlignment: StyleParamType.Number;
  submitButtonPadding: StyleParamType.Number;
  recaptchaAlignment: StyleParamType.Number;
};

export default createStylesParams<ILayoutParams>({
  socialButtonsFirst: {
    type: StyleParamType.Number,
    getDefaultValue: () => LAYOUT_DEFAULTS.socialButtonsFirst,
    metadata: {
      type: StyleType.customEnum,
      defaultValue: {
        type: 'static',
        value: LAYOUT_DEFAULTS.socialButtonsFirst,
      },
      options: {
        type: CSS_PROPERTIES.CSS_DATA_TYPE.number,
        items: [
          { value: 0, displayName: 'First (above form)' },
          { value: 1, displayName: 'Last (below form)' },
        ],
      },
      ai: {
        description:
          'Whether social login buttons appear above or below the email form',
      },
    },
  },
  socialButtonsStyle: {
    type: StyleParamType.Number,
    getDefaultValue: () => LAYOUT_DEFAULTS.socialButtonsStyle,
    metadata: {
      type: StyleType.customEnum,
      defaultValue: {
        type: 'static',
        value: LAYOUT_DEFAULTS.socialButtonsStyle,
      },
      options: {
        type: CSS_PROPERTIES.CSS_DATA_TYPE.number,
        items: [
          { value: 0, displayName: 'Stack' },
          { value: 1, displayName: 'Row' },
          { value: 2, displayName: 'Icons' },
        ],
      },
      ai: {
        description:
          'Layout style for social login buttons: stacked vertically, in a row, or as icon-only buttons',
      },
    },
  },
  socialButtonsSpacing: {
    type: StyleParamType.Number,
    getDefaultValue: () => 16,
    metadata: {
      type: StyleType.gap,
      defaultValue: { type: 'static', value: 16 },
      ai: { description: 'Spacing between social login buttons in pixels' },
    },
  },
  spacingBetweenFormElements: {
    type: StyleParamType.Number,
    getDefaultValue: () => 16,
    metadata: {
      type: StyleType.gap,
      defaultValue: { type: 'static', value: 16 },
      ai: { description: 'Vertical spacing between form sections in pixels' },
    },
  },
  sidesPadding: {
    type: StyleParamType.Number,
    getDefaultValue: ({ isMobile }) =>
      getSidesPaddingDefault({
        isEditor3: isEditor3EditorContext(),
        isStudio2: isStudio2EditorContext(),
        isMobile,
      }),
    inheritDesktop: false,
    metadata: {
      type: StyleType.paddingLeft,
      // Metadata is used by Harmony for Aria compatibility. Runtime defaults
      // are resolved separately by getDefaultValue().
      defaultValue: { type: 'static', value: 24 },
      ai: {
        description:
          'Horizontal padding on the left and right sides of the form',
      },
    },
  },
  topAndBottomPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 60,
    metadata: {
      type: StyleType.paddingTop,
      defaultValue: { type: 'static', value: 60 },
      ai: { description: 'Vertical padding at the top and bottom of the form' },
    },
  },
  fullSubmitButtonWidth: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => LAYOUT_DEFAULTS.fullSubmitButtonWidth,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: LAYOUT_DEFAULTS.fullSubmitButtonWidth,
      },
      ai: {
        description:
          'Whether the submit button stretches to full width of the form',
      },
    },
  },
  logoSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 120,
    metadata: {
      type: StyleType.width,
      defaultValue: { type: 'static', value: 120 },
      ai: { description: 'Display size of the logo image in pixels' },
    },
  },
  logoAlignment: {
    type: StyleParamType.Number,
    getDefaultValue: () => AlignmentOptions.CENTER,
    metadata: {
      type: StyleType.customEnum,
      defaultValue: { type: 'static', value: AlignmentOptions.CENTER },
      options: {
        type: CSS_PROPERTIES.CSS_DATA_TYPE.number,
        items: [
          { value: AlignmentOptions.LEFT, displayName: 'Left' },
          { value: AlignmentOptions.CENTER, displayName: 'Center' },
          { value: AlignmentOptions.RIGHT, displayName: 'Right' },
        ],
      },
      ai: { description: 'Horizontal alignment of the logo image' },
    },
  },
  submitButtonAlignment: {
    type: StyleParamType.Number,
    getDefaultValue: () => AlignmentOptions.CENTER,
    metadata: {
      type: StyleType.customEnum,
      defaultValue: { type: 'static', value: AlignmentOptions.CENTER },
      options: {
        type: CSS_PROPERTIES.CSS_DATA_TYPE.number,
        items: [
          { value: AlignmentOptions.LEFT, displayName: 'Left' },
          { value: AlignmentOptions.CENTER, displayName: 'Center' },
          { value: AlignmentOptions.RIGHT, displayName: 'Right' },
        ],
      },
      ai: { description: 'Horizontal alignment of the submit button' },
    },
  },
  submitButtonPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 16,
    metadata: {
      type: StyleType.padding,
      defaultValue: { type: 'static', value: 16 },
      ai: { description: 'Internal padding of the submit button in pixels' },
    },
  },
  recaptchaAlignment: {
    type: StyleParamType.Number,
    getDefaultValue: ({ isRTL }) =>
      isRTL ? AlignmentOptions.RIGHT : AlignmentOptions.LEFT,
    metadata: {
      type: StyleType.customEnum,
      defaultValue: { type: 'static', value: AlignmentOptions.LEFT },
      options: {
        type: CSS_PROPERTIES.CSS_DATA_TYPE.number,
        items: [
          { value: AlignmentOptions.LEFT, displayName: 'Left' },
          { value: AlignmentOptions.CENTER, displayName: 'Center' },
          { value: AlignmentOptions.RIGHT, displayName: 'Right' },
        ],
      },
      ai: { description: 'Horizontal alignment of the reCAPTCHA widget' },
    },
  },
});
